/**
 * What the Investigation Workspace builds from rings already read: the entity graph with the
 * severity and "needs a decision" mark of each threat, and the campaigns it offers as a scope.
 * Pure, moved out of `components/c3/InvestigationWorkspace.tsx`. The scope lists, the time
 * scrubber and the dossier still derive their views inside the component; Investigate rebuilds them.
 */
// The file, not the folder's index: the index also loads the d3 drawing engine, which a selector does not need.
import {
  buildCombinedThreeLayerGraph,
  type ForceGraphData,
  type ThreatSeverity,
} from '../../components/c3/correlationGraph/buildCombinedGraph';
import { sourceRefId } from '../../lib/funnel';
import { groupCampaigns } from './campaigns';
import type {
  BusinessElementDocument,
  InfrastructureElementDocument,
  ThreatElementDocument,
  ThreatEventItem,
} from '../domain/types';

/** The incident an action element stands for. */
export function eventForThreatElement(
  el: ThreatElementDocument | undefined,
  eventById: Map<string, ThreatEventItem>
): ThreatEventItem | undefined {
  if (!el) return undefined;
  const tid = sourceRefId(el.threat_event_id);
  return tid ? eventById.get(tid) : undefined;
}

/** A level the graph can draw. Unlike the feed, an unknown level reads as medium, not low. */
export function toGraphSeverity(value: unknown): ThreatSeverity {
  const severity = String(value || '').toLowerCase();
  return severity === 'critical' || severity === 'high' || severity === 'medium' || severity === 'low'
    ? severity
    : 'medium';
}

export function isActiveCampaign(el: ThreatElementDocument): boolean {
  const status = String(el.status || 'open').toLowerCase();
  return !['closed', 'dismissed', 'resolved', 'inactive'].includes(status);
}

/** Gives each threat node the severity of its incident, and clears the mark of one already handled. */
export function enrichGraphThreatMeta(
  graph: ForceGraphData,
  threatDocs: ThreatElementDocument[],
  events: ThreatEventItem[]
): ForceGraphData {
  if (graph.nodes.length === 0) return graph;
  const elById = new Map(threatDocs.filter((el) => el._id).map((el) => [String(el._id), el]));
  const eventById = new Map(events.map((event) => [event._id, event]));
  return {
    ...graph,
    nodes: graph.nodes.map((node) => {
      if (node.layer !== 'threat') return node;
      const el = elById.get(node.id);
      const event = eventForThreatElement(el, eventById);
      const severity = toGraphSeverity(
        event?.detection_risk_level || el?.detection?.detection_risk_level || node.severity
      );
      const eventStatus = String(event?.detection_status || '').toLowerCase();
      const handledByEvent =
        eventStatus === 'remediated' || eventStatus === 'folded' || Boolean(event?.folded);
      const needsDecision = handledByEvent ? false : node.needsDecision;
      return { ...node, severity, needsDecision };
    }),
  };
}

export interface WorkspaceGraphInputs {
  infrastructure: InfrastructureElementDocument[];
  business: BusinessElementDocument[];
  threatElements: ThreatElementDocument[];
  threatEvents: ThreatEventItem[];
}

/** The three-layer entity graph the Workspace draws. */
export function buildWorkspaceGraph(inputs: WorkspaceGraphInputs): ForceGraphData {
  const { infrastructure, business, threatElements, threatEvents } = inputs;
  return enrichGraphThreatMeta(
    buildCombinedThreeLayerGraph(infrastructure, business, threatElements),
    threatElements,
    threatEvents
  );
}

export interface CampaignChoice {
  campaign: ThreatElementDocument;
  /** The campaign's incidents, in the order it lists them. */
  members: ThreatEventItem[];
  /** The three that moved most recently. */
  top: ThreatEventItem[];
}

/** The campaigns the scope menu offers: open ones that join two or more incidents (item 3). */
export function selectCampaignChoices(
  threatElements: ThreatElementDocument[],
  threatEvents: ThreatEventItem[]
): CampaignChoice[] {
  const eventById = new Map(threatEvents.map((event) => [event._id, event]));
  const lastMoved = (event: ThreatEventItem) =>
    Date.parse(event.last_event_time || event.created_at || '');
  return groupCampaigns(threatElements, threatEvents)
    .filter(({ campaign }) => isActiveCampaign(campaign))
    .map(({ campaign, actions }) => {
      const members = actions
        .map((action) => eventForThreatElement(action, eventById))
        .filter((event): event is ThreatEventItem => Boolean(event));
      const top = [...members].sort((a, b) => lastMoved(b) - lastMoved(a)).slice(0, 3);
      return { campaign, members, top };
    });
}
